import { SUPABASE_URL } from '../utils/supabase/url';
import { getAuthHeaderToken, getSupabaseClient } from '../utils/supabase/client';
import type {
  Chat,
  ChatParticipant,
  Message,
  MessageType,
  MessageAttachmentInput,
  MessageContentSearchOptions,
  MessageContentSearchPage,
  MessageContentSearchResult,
  MessagePageOptions,
  MessagePageResult,
  ChatAttachmentUploadResult,
} from '../types/messaging';
import { messageTypeLabel, normaliseChat, normaliseChatType, stripMentions } from '../types/messaging';
// Re-exports keep existing import sites working.
export type { MessagePageOptions, MessagePageResult, ChatAttachmentUploadResult };
import { RealtimeChannel } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { DataService } from './DataService';
import { isLikelyEdgeTransportError, markEdgeBackendAvailable, markEdgeBackendUnavailable } from '../utils/edgeNetworkState';
import * as E2EKeyStore from '../features/messages/services/e2eKeyStore';
import { encryptMessage, decryptMessage } from '../features/messages/services/e2eEncryption';

export type MessagingErrorCode =
  | 'AUTH_REQUIRED'
  | 'ACCESS_DENIED'
  | 'NETWORK'
  | 'INVALID_RESPONSE'
  | 'ENCRYPTION_FAILED'
  | 'REQUEST_FAILED';

export class MessagingServiceError extends Error {
  readonly code: MessagingErrorCode;
  readonly status?: number;

  constructor(
    code: MessagingErrorCode,
    message: string,
    options: { status?: number; cause?: unknown } = {},
  ) {
    super(message);
    this.name = 'MessagingServiceError';
    this.code = code;
    this.status = options.status;
    if (options.cause !== undefined) {
      Object.defineProperty(this, 'cause', { value: options.cause, enumerable: false });
    }
  }
}

export type MessagingRealtimeStatus = 'connecting' | 'connected' | 'reconnecting' | 'error' | 'closed';

const toMessagingError = (error: unknown, fallbackMessage: string): MessagingServiceError => {
  if (error instanceof MessagingServiceError) return error;
  return new MessagingServiceError('NETWORK', fallbackMessage, { cause: error });
};


export const PROJECT_THREAD_NAME_PREFIX = '#';

export class MessagingServiceClass {
  private supabase = getSupabaseClient();
  private activeSubscriptions: Map<string, RealtimeChannel> = new Map();
  private chatMessageChannels: Map<string, RealtimeChannel> = new Map();
  private activeViewers = new Map<string, { chatId: string; visible: boolean; nearBottom: boolean }>();
  private unreadChangeListeners: (() => void)[] = [];
  
  // Deduplication caches
  private getChatsPromise: Map<string, Promise<Chat[]>> = new Map();
  private chatsCache: Map<string, Chat[]> = new Map();
  private getChatPromise: Map<string, Promise<Chat | null>> = new Map();
  private getMessagesPagePromise: Map<string, Promise<MessagePageResult>> = new Map();

  // Every user-initiated edge call must carry the active user's access token.
  // Falling back to the public key turns authentication failures into an
  // anonymous privileged-route request and hides session expiry from the UI.
  private async authToken(): Promise<string> {
    const token = await getAuthHeaderToken();
    if (!token || token === publicAnonKey) {
      throw new MessagingServiceError('AUTH_REQUIRED', 'Your session has expired. Please sign in again.');
    }
    return token;
  }

  private async authenticatedHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
    return {
      ...extra,
      Authorization: `Bearer ${await this.authToken()}`,
    };
  }

  private async edgeError(response: Response, operation: string): Promise<MessagingServiceError> {
    const details = await response.text().catch(() => '');
    const accessDenied = response.status === 401 || response.status === 403;
    return new MessagingServiceError(
      accessDenied ? 'ACCESS_DENIED' : 'REQUEST_FAILED',
      details || `${operation} failed (HTTP ${response.status})`,
      { status: response.status },
    );
  }

  // Must mirror the canonical internal-role set (utils/permissions.ts) —
  // admin/staff alone match no production role.
  private isInternalRole(role?: string | null): boolean {
    return role === 'super_admin' || role === 'internal' || role === 'admin' || role === 'staff';
  }

  private getWorkspaceId(value: any): string | null {
    if (!value) return null;
    return value.workspace_id || value.workspaceId || null;
  }

  private getCompanyId(value: any): string | null {
    if (!value) return null;
    return value.company_id || value.companyId || null;
  }

  private isChatExplicitlyShared(chat: any, workspaceId: string | null, companyId: string | null): boolean {
    const permittedWorkspaces = Array.isArray(chat?.permitted_workspaces) ? chat.permitted_workspaces : [];
    const sharedWithOrgs = Array.isArray(chat?.shared_with_orgs) ? chat.shared_with_orgs : [];

    return Boolean(
      (workspaceId && permittedWorkspaces.includes(workspaceId)) ||
      (companyId && sharedWithOrgs.includes(companyId))
    );
  }

  private isChatVisibleToUser(chat: any, currentUser: any): boolean {
    if (!chat || !currentUser) return false;
    if (this.isInternalRole(currentUser.role)) return true;

    const currentWorkspaceId = this.getWorkspaceId(currentUser);
    const currentCompanyId = this.getCompanyId(currentUser);
    const participants = Array.isArray(chat.participants) ? chat.participants : [];

    const otherExternalParticipants = participants
      .map((participant: any) => participant?.user)
      .filter((participantUser: any) =>
        participantUser &&
        participantUser.id !== currentUser.id &&
        !this.isInternalRole(participantUser.role)
      );

    const hasCrossTenantExternalParticipant = otherExternalParticipants.some((participantUser: any) => {
      const participantWorkspaceId = this.getWorkspaceId(participantUser);
      const participantCompanyId = this.getCompanyId(participantUser);

      if (currentWorkspaceId && participantWorkspaceId) {
        return String(participantWorkspaceId) !== String(currentWorkspaceId);
      }

      if (currentCompanyId && participantCompanyId) {
        return String(participantCompanyId) !== String(currentCompanyId);
      }

      return false;
    });

    if (!hasCrossTenantExternalParticipant) {
      return true;
    }

    return this.isChatExplicitlyShared(chat, currentWorkspaceId, currentCompanyId);
  }

  /**
   * A project-linked task's own Thread panel: the task_card anchor message
   * (if the task has ever had something posted from it) plus every reply to
   * it, in either direction — the same rows Messages' own ThreadPanel would
   * show for that message. Returns `card: null` when nothing's been sent
   * from this task yet.
   */
  async getTaskThread(taskId: string): Promise<{ card: Message | null; replies: Message[] }> {
    const { data: card, error: cardError } = await this.supabase
      .from('messages')
      .select(`*, sender:users(id, name, avatar)`)
      .eq('task_id', taskId)
      .eq('message_type', 'task_card')
      .is('deleted_at', null)
      .maybeSingle();
    if (cardError) throw toMessagingError(cardError, 'Unable to load this task\'s thread.');
    if (!card) return { card: null, replies: [] };

    const { data: replies, error: repliesError } = await this.supabase
      .from('messages')
      .select(`*, sender:users(id, name, avatar)`)
      .eq('parent_message_id', card.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (repliesError) throw toMessagingError(repliesError, 'Unable to load this task\'s thread.');

    return { card: card as unknown as Message, replies: (replies ?? []) as unknown as Message[] };
  }

  /**
   * Find a project thread chat by project ID
   */
  async getProjectThread(projectId: string, userId: string): Promise<Chat | null> {
    const chats = await this.getChats(userId);
    return chats.find(chat => 
      chat.type === 'channel' && 
      typeof chat.name === 'string' && 
      (chat.name.startsWith(`Project Thread • ${projectId} •`) || 
       chat.name.endsWith(` • ${projectId}`))
    ) || null;
  }

  /**
   * Ensure a project thread exists (creates it if it doesn't).
   *
   * Routes through the server-side POST /chat/project-thread endpoint so the
   * server — not the client — computes the correct participant set. This fixes
   * the bug where project members (e.g. super_admin / internal staff) were
   * missing from the chat because the client only passed the IDs it happened
   * to know about. The participantIds parameter is kept for backwards
   * compatibility with callers but is no longer forwarded to the server (the
   * server derives membership from the project's authoritative data).
   */
  async ensureProjectThread(
    appProjectId: string,
    projectName: string,
    userId: string,
    _participantIds?: string[],  // kept for API compat; server now computes membership
  ): Promise<string | null> {
    try {
      // `projectId` (lowercase, no prefix) is the imported Supabase project ref
      // (sbuatwyiqtzloxzljnfa); `appProjectId` is the app-level project UUID.
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/project-thread`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await this.authToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: appProjectId,
          projectName: projectName.trim(),
          userId,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(`[MessagingService.ensureProjectThread] Server error ${res.status}:`, text);
        return null;
      }

      const data = await res.json();
      return typeof data?.chatId === 'string' ? data.chatId : null;
    } catch (error) {
      console.error('[MessagingService.ensureProjectThread] Error:', error);
      return null;
    }
  }

  /**
   * Helper to check if an error is an AbortError
   */
  private isAbortError(error: any): boolean {
    const message = error?.message || String(error);
    return (
      message.includes('AbortError') || 
      message.includes('aborted') || 
      message.includes('signal is aborted') ||
      error?.name === 'AbortError'
    );
  }

  /**
   * Subscribe to local unread count changes (triggered by markAsRead)
   */
  onUnreadChange(callback: () => void) {
    this.unreadChangeListeners.push(callback);
    return () => {
        this.unreadChangeListeners = this.unreadChangeListeners.filter(cb => cb !== callback);
    };
  }

  private notifyUnreadChange() {
      this.unreadChangeListeners.forEach(cb => cb());
  }

  /**
   * Get a single chat by ID (for realtime updates)
   */
  async getChat(chatId: string, userId: string): Promise<Chat | null> {
    const cacheKey = `${chatId}:${userId}`;
    const existingPromise = this.getChatPromise.get(cacheKey);
    if (existingPromise) return existingPromise;

    const promise = (async () => {
      try {
        const { data: currentUser } = await this.supabase
          .from('users')
          .select('id, role, workspace_id, company_id')
          .eq('id', userId)
          .maybeSingle();

        const { data, error } = await this.supabase
          .from('chats')
          .select(`
            *,
            participants:chat_participants(
              user_id,
              user:users(id, name, avatar, email, role, workspace_id, company_id, job_title, position)
            )
          `)
          .eq('id', chatId)
          .single();

        if (error) throw error;
        if (!this.isChatVisibleToUser(data, currentUser || { id: userId })) {
          return null;
        }

        // Unread count via message_reads (per-user receipts). PostgREST can't
        // run SQL subqueries in filters — anti-join instead: left-embed the
        // user's read receipts and keep messages where none exists.
        const { count: unreadCount } = await this.supabase
          .from('messages')
          .select('id, message_reads!left(message_id)', { count: 'exact', head: true })
          .eq('chat_id', chatId)
          .neq('sender_id', userId)
          .is('deleted_at', null)
          .eq('message_reads.user_id', userId)
          .is('message_reads', null);

        return {
          id: data.id,
          name: data.name ?? undefined,
          type: normaliseChatType(data.type),
          client_team: normaliseChat(data).client_team,
          workspace_id: data.workspace_id ?? null,
          permitted_workspaces: data.permitted_workspaces || [],
          shared_with_orgs: data.shared_with_orgs || [],
          encryption_required: Boolean((data as any).encryption_required),
          created_at: data.created_at,
          updated_at: data.updated_at,
          last_message_id: data.last_message_id ?? null,
          last_message_preview: data.last_message_preview ?? null,
          last_message_at: data.last_message_at ?? null,
          last_message_sender: data.last_message_sender ?? null,
          participants: (data.participants ?? []).map((participant: any): ChatParticipant => ({
            chat_id: data.id,
            user_id: participant.user_id,
            user: participant.user
              ? {
                  ...participant.user,
                  avatar: participant.user.avatar ?? undefined,
                  workspaceId: participant.user.workspace_id ?? null,
                  companyId: participant.user.company_id ?? null,
                }
              : undefined,
          })),
          unreadCount: unreadCount || 0
        };
      } catch (error) {
        if (!this.isAbortError(error)) {
          console.error('Error fetching chat:', error);
        }
        return null;
      }
    })();

    this.getChatPromise.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.getChatPromise.delete(cacheKey);
    }
  }

  /**
   * Get all chats for the current user
   */
  async getChats(userId: string, retryCount = 0): Promise<Chat[]> {
    if (!userId) return [];
    
    // Check cache
    const existingPromise = this.getChatsPromise.get(userId);
    if (existingPromise) return existingPromise;

    const promise = (async () => {
      try {
        // Route through the edge function server (service role) instead of
        // making direct Supabase REST calls from the browser — avoids CORS /
        // network failures that can occur in sandboxed preview environments.
        const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/list?userId=${encodeURIComponent(userId)}`;
        
        const res = await fetch(url, {
          headers: await this.authenticatedHeaders(),
        });

        if (!res.ok) {
          throw await this.edgeError(res, 'Loading conversations');
        }

        const data = await res.json();
        const chats = Array.isArray(data) ? (data as Chat[]).map(chat => normaliseChat(chat)) : [];
        this.chatsCache.set(userId, chats);
        markEdgeBackendAvailable();
        return chats;
      } catch (error) {
        if (isLikelyEdgeTransportError(error)) {
          markEdgeBackendUnavailable(error);
          const cachedChats = this.chatsCache.get(userId);
          if (cachedChats) {
            return cachedChats;
          }
        }
        if (!this.isAbortError(error)) {
          console.error('[MessagingService.getChats] Error fetching chats:', error);
        }
        // Retry once on transient network failures
        if (retryCount < 1 && !this.isAbortError(error)) {
          this.getChatsPromise.delete(userId);
          return this.getChats(userId, retryCount + 1);
        }
        throw error;
      }
    })();

    this.getChatsPromise.set(userId, promise);
    try {
      return await promise;
    } finally {
      this.getChatsPromise.delete(userId);
    }
  }

  /**
   * Get messages for a specific chat
   */
  async getMessages(chatId: string): Promise<Message[]> {
    const messages: Message[] = [];
    const seen = new Set<string>();
    let before: string | null = null;
    let hasMore = true;

    // Exports and workflow hydration require the complete conversation, not
    // just the newest page. Guard against a malformed server cursor repeating
    // forever while preserving the server's chronological page order.
    while (hasMore) {
      const page = await this.getMessagesPage(chatId, { before, limit: 200 });
      const uniquePage = page.messages.filter((message) => {
        if (seen.has(message.id)) return false;
        seen.add(message.id);
        return true;
      });
      messages.unshift(...uniquePage);

      hasMore = page.hasMore;
      if (!hasMore) break;
      if (!page.nextCursor || page.nextCursor === before) {
        throw new MessagingServiceError('INVALID_RESPONSE', 'The message history cursor did not advance.');
      }
      before = page.nextCursor;
    }

    return messages;
  }

  /** Search plaintext message bodies across conversations visible to the caller. */
  async searchMessages(
    query: string,
    options: MessageContentSearchOptions = {},
  ): Promise<MessageContentSearchPage> {
    const normalizedQuery = query.replace(/\s+/g, ' ').trim();
    if (normalizedQuery.length < 2) return { results: [], nextOffset: null };

    const params = new URLSearchParams({ q: normalizedQuery });
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/message-search?${params.toString()}`,
      {
        headers: await this.authenticatedHeaders(),
        signal: options.signal,
      },
    );

    if (!response.ok) throw await this.edgeError(response, 'Searching messages');
    const payload = await response.json();
    if (!payload || !Array.isArray(payload.results)) {
      throw new MessagingServiceError('INVALID_RESPONSE', 'Message search returned an invalid response.');
    }

    const results = payload.results.filter((result: unknown): result is MessageContentSearchResult => {
      if (!result || typeof result !== 'object') return false;
      const candidate = result as Partial<MessageContentSearchResult>;
      return typeof candidate.messageId === 'string' &&
        typeof candidate.chatId === 'string' &&
        typeof candidate.chatName === 'string' &&
        typeof candidate.snippet === 'string' &&
        typeof candidate.createdAt === 'string';
    });

    return {
      results,
      nextOffset: typeof payload.nextOffset === 'number' ? payload.nextOffset : null,
    };
  }

  private normalizeMessagesPayload(payload: any): MessagePageResult {
    if (Array.isArray(payload)) {
      // Backward-compatible server payload
      return {
        messages: payload as Message[],
        hasMore: false,
        nextCursor: null,
      };
    }

    if (payload && typeof payload === 'object' && Array.isArray(payload.messages)) {
      return {
        messages: payload.messages as Message[],
        hasMore: Boolean(payload.hasMore),
        nextCursor: typeof payload.nextCursor === 'string' ? payload.nextCursor : null,
      };
    }

    throw new MessagingServiceError('INVALID_RESPONSE', 'The server returned an invalid message history response.');
  }

  async getMessagesPage(chatId: string, options: MessagePageOptions = {}): Promise<MessagePageResult> {
    const before = options.before || null;
    const parsedLimit = Number(options.limit ?? 50);
    const limit = Number.isFinite(parsedLimit) ? Math.min(Math.max(parsedLimit, 1), 200) : 50;
    const cacheKey = `${chatId}:${before ?? 'latest'}:${limit}${options.messageId ? `:message:${options.messageId}` : ''}`;
    // A discussion link is an actor-scoped one-shot read; it must not reuse an earlier account's request.
    const existingPromise = options.messageId ? undefined : this.getMessagesPagePromise.get(cacheKey);
    if (existingPromise) return existingPromise;

    const promise = (async () => {
      try {
        // Resolve the current user's ID from the active session.
        // We need it to (a) verify participation server-side and
        // (b) set the isMe flag on the returned messages.
        const { data: { user }, error: userError } = await this.supabase.auth.getUser();
        const userId = user?.id;
        if (userError || !userId) {
          throw new MessagingServiceError('AUTH_REQUIRED', 'Your session has expired. Please sign in again.', {
            cause: userError,
          });
        }

        // Use the backend endpoint so sender names are resolved with a simple
        // batch lookup — no FK constraint or PostgREST join hint required.
        const params = new URLSearchParams({
          userId,
          limit: String(limit),
        });
        if (before) {
          params.set('before', before);
        }
        if (options.messageId) params.set('messageId', options.messageId);
        const url =
          `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/messages/${encodeURIComponent(chatId)}?${params.toString()}`;

        const res = await fetch(url, {
          headers: await this.authenticatedHeaders(),
        });

        if (!res.ok) {
          throw await this.edgeError(res, 'Loading messages');
        }

        const payload = await res.json();
        const page = this.normalizeMessagesPayload(payload);

        // Transparently decrypt encrypted messages.
        const encryptedMessages = page.messages.filter((m) => m.encrypted);
        if (encryptedMessages.length > 0) {
          const chatKey = await E2EKeyStore.getChatKey(chatId, userId);
          for (const msg of page.messages) {
            if (!msg.encrypted) continue;
            if (chatKey) {
              try {
                msg.content = await decryptMessage(msg.content, chatKey);
              } catch {
                msg.content = '[encrypted message]';
              }
            } else {
              msg.content = '[encrypted message]';
            }
            msg.encrypted = false;
          }
        }

        return page;
      } catch (error) {
        if (!this.isAbortError(error)) {
          console.error('Error fetching messages:', error);
        }
        throw toMessagingError(error, 'Unable to load messages. Check your connection and try again.');
      }
    })();

    if (!options.messageId) this.getMessagesPagePromise.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.getMessagesPagePromise.delete(cacheKey);
    }
  }

  private async encryptContentIfConfigured(
    chatId: string,
    userId: string,
    content: string,
  ): Promise<{ content: string; encrypted: boolean }> {
    const chatKey = await E2EKeyStore.getChatKey(chatId, userId);
    if (!chatKey) {
      const requirement = await E2EKeyStore.getChatEncryptionRequirement(chatId, userId);
      if (requirement !== 'legacy') {
        throw new MessagingServiceError(
          'ENCRYPTION_FAILED',
          requirement === 'required'
            ? 'The encryption key for this conversation is unavailable. The message was not sent.'
            : 'Encryption status could not be verified. The message was not sent.',
        );
      }
      return { content, encrypted: false };
    }
    if (!content) return { content, encrypted: false };

    try {
      return {
        content: await encryptMessage(content, chatKey),
        encrypted: true,
      };
    } catch (error) {
      // A provisioned encrypted chat must never silently downgrade this one
      // message to plaintext. Let the caller show a failed-send state instead.
      throw new MessagingServiceError(
        'ENCRYPTION_FAILED',
        'This message could not be encrypted and was not sent.',
        { cause: error },
      );
    }
  }

  private async decryptMessageForUser(message: Message, userId: string): Promise<Message> {
    if (!message.encrypted) return message;
    const chatKey = await E2EKeyStore.getChatKey(message.chat_id, userId);
    if (!chatKey) {
      return { ...message, content: '[encrypted message]', encrypted: false };
    }
    try {
      return {
        ...message,
        content: await decryptMessage(message.content, chatKey),
        encrypted: false,
      };
    } catch {
      return { ...message, content: '[encrypted message]', encrypted: false };
    }
  }

  /**
   * Send a plain-text message. Content must be plain text — never JSON.
   * E2E encryption applies to `content` only; attachment columns are stored
   * plaintext (accepted limitation: key exchange happens per chat, not per field).
   */
  async sendMessage(chatId: string, senderId: string, content: string): Promise<Message | null> {
    try {
      const encryptedContent = await this.encryptContentIfConfigured(chatId, senderId, content);
      const { data, error } = await this.supabase
        .from('messages')
        .insert({
          chat_id: chatId,
          sender_id: senderId,
          content: encryptedContent.content,
          message_type: 'text',
          created_at: new Date().toISOString(),
          encrypted: encryptedContent.encrypted,
        })
        .select(`*, sender:users(id, name, avatar)`)
        .single();

      if (error) throw error;

      // Update chat's updated_at timestamp
      await this.supabase
        .from('chats')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', chatId);

      // ── APNs Push to other participants (fire-and-forget) ──────────────
      // Always pass the original plaintext to the push pipeline.
      this.sendMessagePushNotifications(chatId, senderId, content, data?.sender?.name, data?.id).catch(
        (err) => console.warn('[MessagingService] APNs push failed (non-critical):', err)
      );

      // Return the message with plaintext content so the local UI renders correctly.
      return encryptedContent.encrypted
        ? { ...data, content, encrypted: false } as unknown as Message
        : data as unknown as Message;
    } catch (error) {
      console.error('Error sending message:', error);
      throw toMessagingError(error, 'Unable to send the message.');
    }
  }

  /**
   * Send a structured message (image, video, audio, file, location, or reply).
   * Writes directly to the new structured columns instead of JSON-in-content.
   * Attachment columns are stored plaintext; only `content` (caption / text body)
   * is eligible for E2E encryption.
   */
  async sendStructuredMessage(
    chatId: string,
    senderId: string,
    fields: {
      content?: string;
      messageType: MessageType;
      attachment?: MessageAttachmentInput;
      location?: { lat: number; lng: number };
      parentMessageId?: string;
      /** Set only when messageType is 'task_card' — anchors this message as
       *  the task's connection point into this chat. */
      taskId?: string;
    }
  ): Promise<Message | null> {
    const plainContent = fields.content ?? '';

    // Human-readable push preview: prefer text body, fall back to attachment name, then type label.
    const pushPreview =
      plainContent ||
      fields.attachment?.name ||
      messageTypeLabel(fields.messageType) ||
      'Message';

    try {
      const encryptedContent = await this.encryptContentIfConfigured(chatId, senderId, plainContent);
      const { data, error } = await this.supabase
        .from('messages')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .insert({
          chat_id: chatId,
          sender_id: senderId,
          content: encryptedContent.content,
          message_type: fields.messageType,
          parent_message_id: fields.parentMessageId ?? null,
          // Attachment columns
          attachment_url: fields.attachment?.url ?? null,
          attachment_open_url: fields.attachment?.openUrl ?? null,
          attachment_preview_url: fields.attachment?.previewUrl ?? null,
          attachment_poster: fields.attachment?.poster ?? null,
          attachment_name: fields.attachment?.name ?? null,
          attachment_size: fields.attachment?.size ?? null,
          attachment_mime: fields.attachment?.mime ?? null,
          attachment_duration: fields.attachment?.duration ?? null,
          attachment_drive_file_id: fields.attachment?.driveFileId ?? null,
          // Location columns
          location_lat: fields.location?.lat ?? null,
          location_lng: fields.location?.lng ?? null,
          task_id: fields.taskId ?? null,
          created_at: new Date().toISOString(),
          encrypted: encryptedContent.encrypted,
        })
        .select(`*, sender:users(id, name, avatar)`)
        .single();

      if (error) throw error;

      // Update chat's updated_at timestamp
      await this.supabase
        .from('chats')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', chatId);

      // ── APNs Push (fire-and-forget) ────────────────────────────────────
      this.sendMessagePushNotifications(chatId, senderId, pushPreview, data?.sender?.name, data?.id).catch(
        (err) => console.warn('[MessagingService] APNs push failed (non-critical):', err)
      );

      // Return the message with plaintext content so local UI renders correctly.
      return encryptedContent.encrypted
        ? { ...data, content: plainContent, encrypted: false } as unknown as Message
        : data as unknown as Message;
    } catch (error) {
      console.error('Error sending structured message:', error);
      throw toMessagingError(error, 'Unable to send the message.');
    }
  }

  /**
   * Upload a chat attachment to the chat Drive folder.
   * Returns the uploaded file metadata used by structured attachment messages.
   */
  async uploadChatAttachment(
    chatId: string,
    userId: string,
    file: File
  ): Promise<ChatAttachmentUploadResult | null> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url =
        `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/upload` +
        `?userId=${encodeURIComponent(userId)}&chatId=${encodeURIComponent(chatId)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: await this.authenticatedHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const details = await response.text().catch(() => '');
        throw new Error(details || `Upload failed (HTTP ${response.status})`);
      }

      const payload = (await response.json()) as ChatAttachmentUploadResult;
      if (!payload?.success || !payload?.driveFileId) {
        throw new Error('Upload succeeded but file metadata is incomplete');
      }

      return payload;
    } catch (error) {
      console.error('Error uploading chat attachment:', error);
      return null;
    }
  }

  /**
   * Edit an existing message (only the sender can edit their own messages)
   */
  async editMessage(messageId: string, senderId: string, newContent: string): Promise<Message | null> {
    try {
      const { data: existingMessage, error: existingError } = await this.supabase
        .from('messages')
        .select('id, chat_id, sender_id, encrypted')
        .eq('id', messageId)
        .eq('sender_id', senderId)
        .single();

      if (existingError) throw existingError;
      const encryptedContent = existingMessage.encrypted
        ? await this.encryptContentIfConfigured(existingMessage.chat_id, senderId, newContent)
        : { content: newContent, encrypted: false };

      if (existingMessage.encrypted && !encryptedContent.encrypted) {
        throw new MessagingServiceError(
          'ENCRYPTION_FAILED',
          'The encryption key for this message is unavailable. The edit was not saved.',
        );
      }

      const { data, error } = await this.supabase
        .from('messages')
        .update({
          content: encryptedContent.content,
          encrypted: encryptedContent.encrypted,
          edited_at: new Date().toISOString(),
        })
        .eq('id', messageId)
        .eq('sender_id', senderId) // Safety: only sender can edit
        .select(`*, sender:users(id, name, avatar)`)
        .single();

      if (error) throw error;
      const result = {
        ...data,
        content: newContent,
        encrypted: false,
        sender: data.sender
          ? {
              ...data.sender,
              avatar: data.sender.avatar ?? undefined,
            }
          : undefined,
      };
      return result;
    } catch (error) {
      console.error('Error editing message:', error);
      if (error instanceof MessagingServiceError) throw error;
      return null;
    }
  }

  /**
   * Send APNs push notifications to all participants of a chat
   * except the sender. Runs as fire-and-forget so it never blocks
   * the message send flow.
   */
  private async sendMessagePushNotifications(
    chatId: string,
    senderId: string,
    content: string,
    senderName?: string,
    messageId?: string,
  ): Promise<void> {
    try {
      // 1. Fetch all participants for this chat
      const { data: participants, error } = await this.supabase
        .from('chat_participants')
        .select('user_id')
        .eq('chat_id', chatId);

      if (error) return;

      // 2. Extract mentioned user IDs from content [[user:id:Name]]
      const mentionedUserIds: string[] = [];
      const userMentionRegex = /\[\[user:([^:]+):[^\]]+\]\]/g;
      let match;
      while ((match = userMentionRegex.exec(content)) !== null) {
        mentionedUserIds.push(match[1]);
      }

      // 3. Combine participants and mentioned users
      const allRecipientIds = new Set([
        ...(participants || []).map((p: any) => p.user_id),
        ...mentionedUserIds
      ]);

      // 4. Filter out the sender — only notify others
      const recipientIds = Array.from(allRecipientIds).filter((uid: string) => uid !== senderId);

      if (recipientIds.length === 0) return;

      const mentionedRecipients = recipientIds.filter((uid) => mentionedUserIds.includes(uid));
      const directMessageRecipients = recipientIds.filter((uid) => !mentionedUserIds.includes(uid));

      // 5. Resolve sender name if not provided (fallback)
      let displayName = senderName;
      if (!displayName) {
        try {
          const { data: senderData } = await this.supabase
            .from('users')
            .select('name')
            .eq('id', senderId)
            .single();
          displayName = senderData?.name || 'Someone';
        } catch {
          displayName = 'Someone';
        }
      }
      const resolvedDisplayName = displayName || 'Someone';

      // 6. Clean content for push preview (strip rich formatting markers)
      const pushBody = this.extractPushPreview(content);

      // 7. Create persisted notifications. The authenticated notification
      // route owns APNs/web delivery after it verifies the stored message and
      // recipients; clients must never submit arbitrary lock-screen copy.
      const now = new Date().toISOString();
      const notificationsToInsert = [
        ...directMessageRecipients.map((uid) => ({
          id: crypto.randomUUID(),
          user_id: uid,
          type: 'chat_message',
          priority: 'normal',
          read: false,
          title: resolvedDisplayName,
          message: pushBody,
          related_entity_type: 'message',
          related_entity_id: messageId || chatId,
          action_url: '/messages',
          metadata: { type: 'chat_message', chatId, messageId, senderId, senderName: resolvedDisplayName },
          created_at: now,
        })),
        ...mentionedRecipients.map((uid) => ({
          id: crypto.randomUUID(),
          user_id: uid,
          type: 'activity_mention',
          priority: 'high',
          read: false,
          title: 'You were mentioned',
          message: `${resolvedDisplayName} mentioned you: ${pushBody}`,
          related_entity_type: 'message',
          related_entity_id: messageId || chatId,
          action_url: '/messages',
          metadata: { type: 'chat_message', chatId, messageId, senderId, senderName: resolvedDisplayName },
          created_at: now,
        })),
      ];
      if (notificationsToInsert.length > 0) {
        // Route through the edge function (service role) — the notifications
        // table has no client INSERT policy, so a direct insert for recipients
        // other than the sender is denied by RLS (403).
        fetch(
          `${SUPABASE_URL}/functions/v1/make-server-a476273b/notifications/batch`,
          {
            method: 'POST',
            headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ notifications: notificationsToInsert }),
          },
        ).catch(() => {});
      }
    } catch (err) {
      // Non-critical — log and swallow
      console.warn('[MessagingService] Push notification dispatch error:', err);
    }
  }

  /**
   * Advance the authenticated participant's read cursor. The server updates
   * chat_participants.last_read_at and keeps only the newest compatibility
   * receipt, so opening a long-running conversation remains O(1).
   */
  async markAsRead(chatId: string, userId: string, observedMessageId?: string): Promise<void> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}/read`;
      const response = await fetch(url, {
        method: 'POST',
        headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
        ...(observedMessageId !== undefined ? { body: JSON.stringify({ observedMessageId }) } : {}),
      });
      // Released clients can briefly run against an older edge deployment.
      // Keep that mixed-version window functional, but never fall back for
      // auth, validation, or server failures.
      if (observedMessageId === undefined && (response.status === 404 || response.status === 405)) {
        await this.markAsReadWithLegacyReceipts(chatId, userId);
        this.notifyUnreadChange();
        return;
      }
      if (!response.ok) {
        throw await this.edgeError(response, 'Marking the conversation as read');
      }

      this.notifyUnreadChange();
    } catch (error) {
      console.error('Error marking read:', error);
      throw toMessagingError(error, 'Unable to update the conversation read status.');
    }
  }

  private async markAsReadWithLegacyReceipts(chatId: string, userId: string): Promise<void> {
    const batchSize = 500;
    let offset = 0;
    while (true) {
      const { data, error } = await this.supabase
        .from('messages')
        .select('id')
        .eq('chat_id', chatId)
        .neq('sender_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .range(offset, offset + batchSize - 1);
      if (error) throw error;

      const rows = data ?? [];
      if (rows.length > 0) {
        const readAt = new Date().toISOString();
        const { error: receiptError } = await this.supabase
          .from('message_reads')
          .upsert(
            rows.map(({ id }) => ({ message_id: id, user_id: userId, read_at: readAt })),
            { onConflict: 'message_id,user_id', ignoreDuplicates: true },
          );
        if (receiptError) throw receiptError;
      }

      if (rows.length < batchSize) break;
      offset += batchSize;
    }
  }

  /**
   * Soft-delete a message.
   * forEveryone=true sets deleted_for_everyone and the trigger will show a tombstone to all.
   * forEveryone=false hides only for the requesting user (future: per-user hide table).
   */
  async softDeleteMessage(messageId: string, userId: string, forEveryone = false): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('messages')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: userId,
          deleted_for_everyone: forEveryone,
        })
        .eq('id', messageId)
        .eq('sender_id', userId); // Only sender can delete their own message

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error soft-deleting message:', error);
      return false;
    }
  }

  /**
   * Create a new chat (direct)
   * Uses a server-side RPC function to handle RLS safely and atomically
   */
  async createDirectChat(userIds: string[]): Promise<string | null> {
    try {
      // Filter out null/undefined/empty IDs to prevent DB errors
      const validIds = userIds.filter(id => id && id.trim().length > 0);
      
      if (validIds.length === 0) {
        console.error('[MessagingService] Cannot create chat: No valid participants provided');
        return null;
      }

      // Use RPC to avoid RLS "Chicken and Egg" issues where you can't see the chat you just created
      const { data, error } = await this.supabase
        .rpc('create_new_chat', {
          _type: 'direct',
          _name: '',
          _participant_ids: validIds
        });

      if (error) {
        console.error('[MessagingService] RPC Error in create_new_chat:', error);
        throw error;
      }
      
      return data; // Returns the UUID of the new chat
    } catch (error) {
      console.error('Error creating direct chat:', error);
      return null;
    }
  }

  /** Direct chat through the server's eligibility check (same workspace,
   * internal, or same company). Used for bots, which the client-side RPC
   * path does not know about. */
  async createDirectChatViaServer(requesterId: string, targetUserId: string): Promise<string | null> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/direct?userId=${encodeURIComponent(requesterId)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ targetUserId }),
      });
      if (!res.ok) {
        console.error('[MessagingService.createDirectChatViaServer] Server error:', res.status, await res.text());
        return null;
      }
      const data = await res.json();
      return typeof data?.chatId === 'string' ? data.chatId : null;
    } catch (error) {
      console.error('Error creating direct chat via server:', error);
      return null;
    }
  }

  /**
   * Create (or reuse) the commercial workspace chat.
   * The DB function derives the caller's workspace, gathers all workspace
   * members as participants, names the chat after the workspace, and
   * deduplicates so only one commercial chat exists per workspace.
   */
  async createCommercialChat(): Promise<string | null> {
    try {
      const { data, error } = await this.supabase
        .rpc('create_commercial_chat');

      if (error) {
        console.error('[MessagingService] RPC Error creating commercial chat:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating commercial chat:', error);
      return null;
    }
  }

  /**
   * Create a group chat channel
   */
  async createGroupChat(userIds: string[], name: string): Promise<string | null> {
    const validIds = Array.from(new Set(userIds.filter(id => id && id.trim().length > 0)));

    if (validIds.length === 0) {
      throw new Error('No valid participants provided');
    }

    const { data, error } = await this.supabase
      .rpc('create_new_chat', {
        _type: 'channel',
        _name: name || '',
        _participant_ids: validIds
      });

    if (error) {
      console.error('[MessagingService] RPC Error creating group chat:', error);
      throw error;
    }

    return data;
  }

  async renameChat(chatId: string, userId: string, name: string): Promise<boolean> {
    try {
      const trimmedName = name.trim();
      if (!trimmedName) return false;

      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${await this.authToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: trimmedName }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error(`[MessagingService] Rename failed: ${res.status}`, text);
        return false;
      }

      const data = await res.json();
      if (data.success === false) {
        console.warn('[MessagingService] Rename returned success: false', data.message);
        return false;
      }

      this.notifyUnreadChange();
      return true;
    } catch (error) {
      console.error('[MessagingService] Error renaming chat:', error);
      return false;
    }
  }

  /**
   * Add a participant to a chat
   */
  async addParticipant(chatId: string, targetUserId: string, requesterId: string): Promise<boolean> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}/participants` +
        `?userId=${encodeURIComponent(requesterId)}&targetUserId=${encodeURIComponent(targetUserId)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${await this.authToken()}` },
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('[MessagingService.addParticipant] Server error:', res.status, text);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error adding participant:', error);
      return false;
    }
  }

  async removeParticipant(chatId: string, targetUserId: string): Promise<boolean> {
    try {
      // Reuse the existing DELETE /:chatId?userId=<targetUserId> endpoint (service role, bypasses RLS)
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}` +
        `?userId=${encodeURIComponent(targetUserId)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: await this.authenticatedHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('[MessagingService.removeParticipant] Server error:', res.status, text);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error removing participant:', error);
      return false;
    }
  }

  /**
   * Hard-delete a chat for ALL participants (admin only).
   * Passes force=true so the backend permanently destroys the record.
   */
  async hardDeleteChat(chatId: string, userId: string): Promise<boolean> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}?userId=${encodeURIComponent(userId)}&force=true`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await this.authToken()}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const text = await res.text();
        console.error(`[MessagingService] Hard delete failed: ${res.status}`, text);
        return false;
      }
      const data = await res.json();
      if (data.success === false) {
        console.warn('[MessagingService] Hard delete returned success: false', data.message);
        return false;
      }
      this.unsubscribeFromChat(chatId);
      this.notifyUnreadChange();
      return true;
    } catch (error) {
      console.error('[MessagingService] Error hard-deleting chat:', error);
      return false;
    }
  }

  /**
   * Leave a chat (removes the current user as a participant).
   * Calls the backend server which uses SERVICE_ROLE_KEY to bypass RLS.
   */
  async deleteChat(chatId: string, userId: string): Promise<boolean> {
    try {
      console.log(`[MessagingService] Deleting chat ${chatId} for user ${userId}`);
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}?userId=${encodeURIComponent(userId)}`;
      
      const res = await fetch(url, {
        method: 'DELETE',
        headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error(`[MessagingService] Delete failed: ${res.status}`, text);
        return false;
      }

      const data = await res.json();
      if (data.success === false) {
        console.warn('[MessagingService] Delete returned success: false', data.message);
        return false;
      }

      console.log(`[MessagingService] Chat ${chatId} successfully deleted for user ${userId}`);
      
      // Also unsubscribe if active
      this.unsubscribeFromChat(chatId);
      
      // Notify listeners to refresh the chat list
      this.notifyUnreadChange();
      
      return true;
    } catch (error) {
      console.error('[MessagingService] Error deleting chat via backend:', error);
      return false;
    }
  }

  /**
   * Hide a chat for the current user only ("delete for me"). Membership and
   * messages are untouched — other participants see no change — and the
   * a new message after the retained cutoff resurfaces the conversation without
   * restoring history that this user deleted.
   */
  async hideChat(chatId: string, userId: string): Promise<boolean> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}/hide?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!res.ok) {
        console.error(`[MessagingService] Hide failed: ${res.status}`, await res.text());
        return false;
      }
      const data = await res.json();
      return data.success !== false;
    } catch (error) {
      console.error('[MessagingService] Error hiding chat via backend:', error);
      return false;
    }
  }

  /**
   * Mark a chat as unread for the current user (iOS Mail-style toggle).
   * Sets a per-user flag the list route surfaces as unreadCount >= 1;
   * cleared again by the next markAsRead.
   */
  async markChatAsUnread(chatId: string, userId: string): Promise<boolean> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}/unread?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: await this.authenticatedHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!res.ok) {
        console.error(`[MessagingService] Mark unread failed: ${res.status}`, await res.text());
        return false;
      }
      const data = await res.json();
      return data.success !== false;
    } catch (error) {
      console.error('[MessagingService] Error marking chat unread via backend:', error);
      return false;
    }
  }

  /**
   * Send a typing indicator event via Supabase Realtime broadcast
   */
  sendTypingEvent(chatId: string, userId: string): void {
    const channel = this.activeSubscriptions.get(chatId);
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId }
      });
    }
  }

  setChatViewingState(
    viewerId: string,
    chatId: string,
    state: { visible: boolean; nearBottom: boolean },
  ): void {
    this.activeViewers.set(viewerId, { chatId, ...state });
  }

  clearChatViewingState(viewerId: string): void {
    this.activeViewers.delete(viewerId);
  }

  isChatActivelyViewed(chatId: string): boolean {
    for (const viewer of this.activeViewers.values()) {
      if (viewer.chatId === chatId && viewer.visible && viewer.nearBottom) return true;
    }
    return false;
  }

  private mapRealtimeStatus(status: string): MessagingRealtimeStatus {
    switch (status) {
      case 'SUBSCRIBED': return 'connected';
      case 'CLOSED': return 'closed';
      case 'TIMED_OUT': return 'reconnecting';
      case 'CHANNEL_ERROR': return 'error';
      default: return 'connecting';
    }
  }

  /**
   * Provisions E2E chat keys for all participants who have published public keys.
   * No-op if already provisioned. Call when opening a chat.
   */
  async provisionChatKeyIfNeeded(chatId: string, userId: string, participantIds: string[]): Promise<void> {
    await E2EKeyStore.provisionChatKeyIfNeeded(chatId, userId, participantIds);
  }

  async toggleReaction(chatId: string, messageId: string, emoji: string, userId: string, userName: string) {
    // message_reactions carries a UNIQUE (message_id, user_id) constraint —
    // one reaction per user per message, iMessage tapback-style. Picking the
    // same emoji again clears it; picking a different one replaces it.
    const { data: existing, error: existingError } = await this.supabase
      .from('message_reactions')
      .select('id, emoji')
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .maybeSingle();
    if (existingError) throw toMessagingError(existingError, 'Unable to react to this message.');

    if (existing?.emoji === emoji) {
      const { error } = await this.supabase.from('message_reactions').delete().eq('id', existing.id);
      if (error) throw toMessagingError(error, 'Unable to remove your reaction.');
    } else {
      const { error } = await this.supabase
        .from('message_reactions')
        .upsert(
          { message_id: messageId, user_id: userId, emoji } as any,
          { onConflict: 'message_id,user_id' },
        );
      if (error) throw toMessagingError(error, 'Unable to react to this message.');
    }

    const channel = this.activeSubscriptions.get(chatId);
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'reaction',
        payload: { messageId, emoji, userId, userName }
      });
    }

    // Send push notification to the message author (fire-and-forget)
    this.sendReactionPushNotification(chatId, messageId, emoji, userId, userName).catch(
      (err) => console.warn('[MessagingService] Reaction APNs push failed (non-critical):', err)
    );
  }

  /**
   * Send APNs push notification to the author of a message when someone reacts
   */
  private async sendReactionPushNotification(
    chatId: string,
    messageId: string,
    emoji: string,
    reactorUserId: string,
    reactorUserName?: string,
  ): Promise<void> {
    // Reactions are intentionally kept out of APNs to avoid low-signal push noise.
    // The underlying chat/message UI already reflects reactions in real time.
    void chatId;
    void messageId;
    void emoji;
    void reactorUserId;
    void reactorUserName;
  }

  /**
   * Subscribe to new messages and typing events for a specific chat
   */
  subscribeToChat(
    chatId: string,
    userId: string,
    onNewMessage: (msg: Message) => void,
    onTyping: (userId: string) => void,
    onMessageUpdated?: (msg: Message) => void,
    onReaction?: (messageId: string, emoji: string, userId: string, userName: string) => void,
    onStatusChange?: (status: MessagingRealtimeStatus) => void,
  ): void {
    // Unsubscribe from any existing subscription for this chat
    this.unsubscribeFromChat(chatId);

    // Message RLS also admits authorised nonparticipants. A denied private
    // broadcast join must not prevent their message inserts and edits arriving.
    const messageChannel = this.supabase
      .channel(`chat-messages:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`
        },
        async (payload: any) => {
          const newMsg = payload.new as Message;

          // Own messages are NOT skipped: the same account sends from other
          // devices (iPhone/iPad/Mac), and those must land in this tab live
          // ("messages sent from the native users not showing in the
          // browser"). A send from THIS tab is harmless — every consumer
          // dedupes by message id, and the send path adds the real row, so
          // either arrival order collapses to one bubble.

          // Decrypt in-place before handing to the UI
          onNewMessage(await this.decryptMessageForUser(newMsg, userId));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`
        },
        async (payload: any) => {
          const updatedMsg = payload.new as Message;
          // Fire for edits and soft-deletes, not for read_at updates
          if ((updatedMsg.edited_at || updatedMsg.deleted_at) && onMessageUpdated) {
            onMessageUpdated(await this.decryptMessageForUser(updatedMsg, userId));
          }
        }
      )
      .subscribe((status) => onStatusChange?.(this.mapRealtimeStatus(status)));

    const channel = this.supabase
      .channel(`chat:${chatId}`, { config: { private: true } })
      .on(
        'broadcast',
        { event: 'typing' },
        (payload: any) => {
          const typingUserId = payload.payload?.userId;
          if (typingUserId && typingUserId !== userId) {
            onTyping(typingUserId);
          }
        }
      )
      .on(
        'broadcast',
        { event: 'reaction' },
        (payload: any) => {
            const { messageId, emoji, userId: reactionUserId, userName } = payload.payload;
            if (reactionUserId && reactionUserId !== userId) {
                onReaction?.(messageId, emoji, reactionUserId, userName);
            }
        }
      )
      .subscribe();

    this.chatMessageChannels.set(chatId, messageChannel);
    this.activeSubscriptions.set(chatId, channel);
    onStatusChange?.('connecting');
  }

  /**
   * Unsubscribe from a specific chat's realtime channel
   */
  unsubscribeFromChat(chatId: string): void {
    const messageChannel = this.chatMessageChannels.get(chatId);
    if (messageChannel) {
      this.supabase.removeChannel(messageChannel);
      this.chatMessageChannels.delete(chatId);
    }
    const channel = this.activeSubscriptions.get(chatId);
    if (channel) {
      this.supabase.removeChannel(channel);
      this.activeSubscriptions.delete(chatId);
    }
  }

  /**
   * Subscribe to global changes (new messages across all chats, new chats)
   * Used by MessagingContext to keep the chat list in sync
   */
  subscribeToGlobalChanges(
    userId: string,
    onNewMessage: (msg: Message) => void,
    onNewChat: (chatId: string) => void,
    onMessageUpdated?: (msg: Message) => void,
    onStatusChange?: (status: MessagingRealtimeStatus) => void,
  ): RealtimeChannel {
    const channel = this.supabase
      .channel(`global:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload: any) => {
          onNewMessage(await this.decryptMessageForUser(payload.new as Message, userId));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        async (payload: any) => {
          const updatedMsg = payload.new as Message;
          if (updatedMsg.edited_at && onMessageUpdated) {
            onMessageUpdated(await this.decryptMessageForUser(updatedMsg, userId));
          }
        }
      )
      .subscribe((status) => onStatusChange?.(this.mapRealtimeStatus(status)));
    onStatusChange?.('connecting');

    // chat_participants rides its OWN channel: a channel bundling a
    // chat_participants binding never completes its join ack (diagnosed
    // live on the native SDK - the server registers the subscription, the
    // client times out), and one poisoned binding stalls every OTHER
    // binding on the same channel. Split out, the messages bindings above
    // always join; if this one stalls, only instant new-chat detection
    // degrades - and adds now write a system message that the messages
    // binding catches anyway.
    const participantsChannel = this.supabase
      .channel(`global-participants:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_participants',
          filter: `user_id=eq.${userId}`
        },
        (payload: any) => {
          onNewChat(payload.new.chat_id);
        }
      )
      .subscribe();
    this.globalParticipantChannels.set(channel, participantsChannel);

    return channel;
  }

  /** Secondary per-user chat_participants channels, keyed by the primary
   *  global channel handed back to callers, so teardown removes both. */
  private globalParticipantChannels = new Map<RealtimeChannel, RealtimeChannel>();

  /**
   * Lists the caller's own pending scheduled messages for a chat, soonest
   * first. RLS scopes rows to sender_id = auth.uid(), so no user filter.
   */
  async listScheduledMessages(chatId: string): Promise<Array<{
    id: string;
    chat_id: string;
    content: string;
    scheduled_at: string;
  }>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (this.supabase as any)
      .from('scheduled_messages')
      .select('id, chat_id, content, scheduled_at')
      .eq('chat_id', chatId)
      .eq('status', 'pending')
      .order('scheduled_at', { ascending: true });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data ?? []) as any;
  }

  /**
   * Cancels a still-pending scheduled message. RLS restricts the delete to
   * the caller's own rows; a sent row no longer matches status=pending.
   */
  async cancelScheduledMessage(id: string): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (this.supabase as any)
      .from('scheduled_messages')
      .delete()
      .eq('id', id)
      .eq('status', 'pending');
    if (error) throw error;
  }

  /**
   * Unsubscribe from the global realtime channel
   */
  unsubscribeGlobalChanges(channel: RealtimeChannel): void {
    if (channel) {
      this.supabase.removeChannel(channel);
      const participantsChannel = this.globalParticipantChannels.get(channel);
      if (participantsChannel) {
        this.supabase.removeChannel(participantsChannel);
        this.globalParticipantChannels.delete(channel);
      }
    }
  }

  /**
   * Extract a clean, human-readable preview string from any message
   * content format for use in APNs push notification bodies.
   *
   * Handles:
   *   - JSON structured messages: reply, file, image, location, voice
   *   - Mentions: [[user:id:Name]]
   *   - Legacy bracket-style markers: [FILE:…], [LOCATION:…], etc.
   *   - Plain text
   */
  // Long enough that hold-to-preview on iOS shows the whole message for all
  // but essays; well inside the ~4KB push payload limit.
  private static readonly PUSH_PREVIEW_MAX = 1000;

  private extractPushPreview(content: string): string {
    // Callers pass the already-resolved plain-text preview (structured sends
    // build their own label), so content is plain text — just clean it up.
    // Mentions [[kind:id:Name]] -> @Name
    let cleaned = stripMentions(content);

    // Legacy bracket markers (e.g. [FILE:…]) from very old messages.
    cleaned = cleaned
      .replace(/\[REPLY:.*?\]/g, '')
      .replace(/\[FILE:.*?\]/g, '📎 Sent a file')
      .replace(/\[LOCATION:.*?\]/g, '📍 Shared a location')
      .replace(/\[VOICE:.*?\]/g, '🎤 Voice message')
      .trim();

    return cleaned.substring(0, MessagingServiceClass.PUSH_PREVIEW_MAX) || 'Sent a message';
  }
  /**
   * Schedule a message for future delivery.
   * The edge function validates that scheduled_at is at least 60 seconds in the
   * future; the cron flush (fn_flush_scheduled_messages) delivers it.
   * Returns the new scheduled_message_id on success, or null on failure.
   */
  async scheduleMessage(
    chatId: string,
    senderId: string,
    content: string,
    scheduledAt: Date,
  ): Promise<{ scheduled_message_id: string } | null> {
    try {
      const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/chat/${encodeURIComponent(chatId)}/message/schedule`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await this.authToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          scheduled_at: scheduledAt.toISOString(),
          sender_id: senderId,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[MessagingService.scheduleMessage] Server error:', res.status, text);
        return null;
      }
      const data = await res.json();
      return data as { scheduled_message_id: string };
    } catch (error) {
      console.error('[MessagingService.scheduleMessage] Error:', error);
      return null;
    }
  }
}

// Singleton export
export const MessagingService = new MessagingServiceClass();
